import * as React from 'react'
import { LayoutRectangle, Platform, StyleSheet, TextStyle, View, ViewProps, ViewStyle } from 'react-native'
import BackgroundLayout from '@bit/jimmijoensson.keyhole.layouts.background-layout'
import InfoCard from '@bit/jimmijoensson.keyhole.components.info-card'
import Divider from '@bit/jimmijoensson.keyhole.components.divider'
import Paragraph from '@bit/jimmijoensson.keyhole.paragraph'
import { theme, themeStyles } from '@bit/jimmijoensson.keyhole.components.keyhole-theme'
import Card from '@bit/jimmijoensson.keyhole.components.card'
import Heading from '@bit/jimmijoensson.keyhole.components.heading'
import Price from '@bit/jimmijoensson.keyhole.components.price'
import BuildingIcon from '@bit/jimmijoensson.keyhole.icons.building-icon'
import { useKeyholeListingContext } from '../../contexts/KeyholeListingContext/KeyholeListingContext'
import { ComplexListing, initialComplexListing } from '@bit/jimmijoensson.keyhole.interfaces.complex-listing-interface'
import { getNonDecimalEnding, isEmptyStr, prettifyUrl } from '../../utils/utils'
import { getComplexDetails } from '../../utils/helpers'
import { useResponsiveContext } from '../../contexts/ResponsiveContext/ResponsiveContext'
import OverlayBoxLayout from '../../layouts/OverlayBoxLayout'
import GoogleMapReact from 'google-map-react'
import Marker from '@bit/jimmijoensson.keyhole.components.marker'

let parsedUrl = new URL(window.location.href)
let parsedParams = new URLSearchParams(parsedUrl.search)

export interface ComplexProps extends ViewProps {
  //Props goes here
}

const Complex = ({
  ...props
}: ComplexProps) => {
  const { dims } = useResponsiveContext()
  const { fetchComplex } = useKeyholeListingContext()
  const [complex, setComplex] = React.useState<ComplexListing>(initialComplexListing)
  const [textWrapperDims, setTextWrapperDims] = React.useState<LayoutRectangle>()
  const [overlayLayoutDims, setOverlayLayoutDims] = React.useState<LayoutRectangle>()
  const [depositOverlayDims, setDepositOverlayDims] = React.useState<LayoutRectangle>()

  const mounted = React.useCallback(async () => {
    const id = parsedParams.get('id')
    if (id) {
      try {
        setComplex(await fetchComplex(id))
      } catch (error) {
        throw error
      }
    } else {}
  }, [fetchComplex])

  React.useEffect(() => {
    mounted()
  }, [mounted])

	return (
		<View
      nativeID="Complex"
      style={styles.wrapper}
      {...props}
    >
      {Platform.OS === 'web' && !isEmptyStr(complex.id) && (
        <div
          id="ComplexData"
          data-complex-name={complex.name}
          data-complex-city={complex.city}
          data-complex-zipcode={complex.zipcode}
          data-complex-short-desc={complex.shortDescription}
        />
      )}
      <BackgroundLayout offset={dims.height * .74 - 74}
        backgroundContent={
          <View style={{width: '100%', height: dims.height * .74}}>
            {(complex.lat && complex.long && (
              <GoogleMapReact
                bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '' }}
                defaultCenter={{
                  lat: Number(complex.lat),
                  lng: Number(complex.long)
                }}
                defaultZoom={15}
                options={{
                  draggable: false,
                  draggableCursor: 'default',
                  fullscreenControl: false,
                  zoomControl: false,
                  clickableIcons: false,
                  styles: [
                    {
                      featureType: "poi",
                      stylers: [
                        { visibility: "off" }
                      ]
                    }
                  ]
                }}
                yesIWantToUseGoogleMapApiInternals
              >
                <Marker
                  lat={Number(complex.lat)}
                  lng={Number(complex.long)}
                  style={[themeStyles.marker, dims.width > theme.breakpoints.md ? styles.marker : styles.markerSm]}
                >
                  <BuildingIcon
                    color={theme.colors.green}
                    style={{height: 22, width: 22}}
                  />
                </Marker>
              </GoogleMapReact>
            )) || undefined }
          </View>
        }
      >
        <View
          style={[
            styles.inner,
            {
              paddingHorizontal: dims.width < 992 ? 20 : 32,
              paddingBottom: dims.width > theme.breakpoints.md ? 144 : undefined
            }
          ]}
        >
          <OverlayBoxLayout
            centered={textWrapperDims && textWrapperDims.height < 100}
            dims={dims}
            onLayout={e => setOverlayLayoutDims(e.nativeEvent.layout)}
            onOverlayLayout={e => setDepositOverlayDims(e.nativeEvent.layout)}
            overlayContent={
              <Card>
                <>
                  <Heading
                    style={[styles.boxTitle, {color: theme.colors.green}]}
                    type="h5"
                  >
                    Indskud
                  </Heading>
                  <Paragraph style={styles.detail}>Depositum:</Paragraph>
                  <Paragraph style={[styles.detail, { fontWeight: '800' }]}>{complex.minimumDeposit.toLocaleString('da-dk')} - {complex.maximumDeposit.toLocaleString('da-dk')} DKK</Paragraph>
                  <Divider
                    noLine
                    style={{
                      marginVertical: 8,
                    }}
                  />
                  <Paragraph style={styles.detail}>Forudbetalt leje:</Paragraph>
                  <Paragraph style={[styles.detail, { fontWeight: '800' }]}>{complex.minimumPrepaid.toLocaleString('da-dk')} - {complex.maximumPrepaid.toLocaleString('da-dk')} DKK</Paragraph>
                  <Divider
                    noLine
                    style={{
                      marginVertical: 28,
                    }}
                  />
                  <Heading
                    style={[styles.boxTitle, {color: theme.colors.red}]}
                    type="h5"
                  >
                    Keyhole deposit
                  </Heading>
                  <View style={
                    dims.width < 375
                      ? styles.depositWrapperXs
                      : styles.depositWrapper
                  }>
                    <Price
                      currency="FRA"
                      currencyWeight="800"
                      currencyOpacity={1}
                      price={complex.minimumMonthlyFee}
                      size={30}
                      subText="DKK/måned"
                      suffix={getNonDecimalEnding('da-DK', complex.minimumMonthlyFee)}
                    />
                    <Divider
                      noLine
                      style={{
                        marginHorizontal: (dims.width < 400 && 16) || 32,
                      }}
                    />
                    <View style={{opacity: .5}}>
                      <Price
                        currency="FRA"
                        currencyWeight="800"
                        currencyOpacity={1}
                        price={complex.minimumSignupFee}
                        size={20}
                        subText="DKK"
                        suffix={getNonDecimalEnding('da-DK', complex.minimumSignupFee)}
                        title="Oprettelse"
                      />
                    </View>
                  </View>
                </>
              </Card>
            }
            style={{ zIndex: 2 }}
          >
            <InfoCard
              desc={complex.shortDescription}
              descStyle={[styles.text, {color: '#fff'}]}
              detailsProps={{
                data: getComplexDetails(complex),
                detailStyle: {color: '#fff'},
              }}
              style={{backgroundColor: theme.colors.red}}
              tagline={(complex.areaName ? complex.areaName + ' | ': '') + `${complex.zipcode} ${complex.city}`}
              taglineStyle={{color: '#fff'}}
              title={complex.name}
              titleStyle={{color: '#fff'}}
            />
          </OverlayBoxLayout>
            <Divider noLine style={{marginVertical: dims.width > theme.breakpoints.md ? 40 : 20}}/>
            <View
              onLayout={e => setTextWrapperDims(e.nativeEvent.layout)}
              style={styles.textWrapper}
            >
              {!isEmptyStr(complex.url) &&
              <>
                <Paragraph style={[styles.contactText, styles.link]}>
                  <a
                    href={complex.url}
                    style={{
                      color: theme.colors.green,
                      textTransform: 'uppercase',
                    }}
                    target="_blank"
                  >
                    {prettifyUrl(complex.url)}
                  </a>
                </Paragraph>
              </>
              }
              {!isEmptyStr(complex.description) &&
                <>
                  <Divider noLine style={{marginVertical: 12}}/>
                  <Paragraph
                    style={[
                      styles.text,
                      {
                        maxWidth: 
                          overlayLayoutDims &&
                          depositOverlayDims &&
                          dims.width > theme.breakpoints.md &&
                          dims.width < 1024
                            ?  overlayLayoutDims.width - depositOverlayDims.width - 32 - 24
                            : 560
                      }
                    ]}
                  >
                    {complex.description}
                  </Paragraph>
                </>
              }
            </View>
          <Divider noLine style={{marginVertical: !isEmptyStr(complex.description) || dims.width < theme.breakpoints.md ? 40 : 80}}/>
          <OverlayBoxLayout
            dims={dims}
            overlayContent={
              <Card
                style={{
                  backgroundColor: theme.colors.green
                }}
              >
                <>
                  <Heading
                    style={[styles.boxTitle, {color: '#fff'}]}
                    type="h5"
                  >
                    Kontakt
                  </Heading>
                  <Paragraph style={styles.contactText}>{complex.contactName}</Paragraph>
                  <Divider noLine />
                  {!isEmptyStr(complex.contactPhoneNumber) && (
                    <>
                      <Paragraph style={[styles.contactText, styles.link]}>
                        <a
                          href={`tel:+45${complex.contactPhoneNumber}`}
                          style={{color: '#fff'}}
                          >
                          +45 {complex.contactPhoneNumber}
                        </a>
                      </Paragraph>
                    </>
                  )}
                  {!isEmptyStr(complex.contactEmail) && (
                    <>
                      <Divider noLine style={{marginVertical: 2}}/>
                      <Paragraph style={[styles.contactText, styles.link]}>
                        <a
                          href={`mailto:${complex.contactEmail}`}
                          style={{color: '#fff'}}
                          >
                          {complex.contactEmail}
                        </a>
                      </Paragraph>
                    </>
                  )}
                  {!isEmptyStr(complex.companyUrl) && (
                    <>
                      <Divider noLine />
                      <Paragraph style={[styles.contactText, styles.link]}>
                        <a
                          href={complex.companyUrl}
                          style={{color: '#fff'}}
                          target="_blank"
                          >
                          {prettifyUrl(complex.companyUrl)}
                        </a>
                      </Paragraph>
                    </>
                  )}
                </>
              </Card>
            }
          >
            <InfoCard
              desc={complex.companyShortDescription}
              logo={{
                position: 'top',
                style: {width: 180},
                url: complex.logoUrl,
              }}
              title={complex.companyName}
              titleStyle={{color: theme.colors.smoothBlack}}
              style={{backgroundColor: theme.colors.green1}}
            />
          </OverlayBoxLayout>
        </View>
      </BackgroundLayout>
		</View>
	)
}

export interface ComplexStyles {
  boxTitle: TextStyle
  contactText: TextStyle
  depositWrapper: ViewStyle
  depositWrapperXs: ViewStyle
  detail: TextStyle
  link: TextStyle
  inner: ViewStyle
  marker: ViewStyle
  markerSm: ViewStyle
  text: TextStyle
  textWrapper: TextStyle
  wrapper: ViewStyle
}

const styles = StyleSheet.create<ComplexStyles>({
  boxTitle: {
    fontStyle: 'italic',
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  contactText: {
    color: '#fff',
    fontSize: 16,
    fontStyle: 'italic',
    fontWeight: '800',
  },
  depositWrapper: {
    alignItems: 'flex-end',
    flexDirection: 'row',
  },
  depositWrapperXs: {},
  detail: {
    fontSize: 16,
    fontStyle: 'italic',
    fontWeight: '400',
  },
  link: {
    textDecorationLine: 'underline',
  },
  inner: {
    maxWidth: 1280,
    marginHorizontal: 'auto',
    width: '100%',
  },
  marker: {
    height: 176,
    transform: [
      {translateX: -88},
      {translateY: -88}
    ],
    width: 176,
  },
  markerSm: {
    height: 132,
    transform: [
      {translateX: -66},
      {translateY: -66}
    ],
    width: 132,
  },
  text: {
    fontSize: 16,
  },
  textWrapper: {
    paddingHorizontal: 24,
  },
  wrapper: {
    // Styles go here
  },
})

export default Complex